import { ILoggingOptions, initializeAllIcons, LayoutSDK } from '@notes/sticky-notes-layout'
import LayoutSDKOptions from '@notes/sticky-notes-layout/dist-es2015/sticky-notes-layout/src/LayoutSDKOptions'
import {
  getErrorAsString,
  IAuthenticatedUserInfo,
  isMsa,
  logAndBuildError,
  O365ExchangeTokenError,
  O365ExchangeTokenFailureCase,
  OneNoteTheme,
  refreshAccessToken,
} from '@notes/webintegrations-common'
import * as React from 'react'
import { tryGetOAuthTokenWithoutUserAction } from '../agave/auth/getAccessTokenInfo'
import { redirectToAuth } from '../init/redirectToAuth'
import { LayoutSDKTelemetryCallbacks } from '../LayoutSDKTelemetryCallbacks'
import { isMsaAccountLogin } from '../lib/urlParserUtils'
import { GetTokenFactory } from '../shared/auth/GetTokenFactory'
import { IAuthContext } from '../shared/auth/IAuthContext'
import { RefreshTokenForSdkActivity } from '../shared/auth/RefreshTokenForSdkActivity'
import { Constants } from '../shared/Constants'
import { GlobalVariables } from '../shared/GlobalVariables'
import { getOTelLoggingUserContextFromUserInformation } from '../shared/logger/getLoggingUserContextFromUserInformation'
import { Log } from '../shared/logger/Log'
import { shouldMockExternalApis } from '../shared/shouldMockExternalApis'
import { getInitialTokensForSDK, refreshTokensForSDK } from './getTokensForSDK'
import { showSessionExpired } from './showSessionExpired'

/**
 * A wrapper for the LayoutSDK that simplifies its interface for Sticky Notes integrations, setting common
 * parameters/callbacks, and exposing common methods
 *
 * TODO (machiam): Suggestion from jogonzal:
 *
 * Idea: If the LayoutSdkWrapper is a "stricter" wrapper, then all of the function calls that happen for the LayoutSdk should happen through it, like for example:
 * StickyNotesFrame should have a reference to the wrapper, not to the LayoutSdk itself
 * Instead of calling getLayoutSDK() to know if the user is signed in or not here,
 *   we should create a method in the frame called "isSignedIn()" in the frame that checks if the instance of LayoutSdkWrapper is defined
 * Instead of calling getLayoutSDK() and then .createNote(), StickyNotesFrame would expose
 *   methods that call the wrapper, that way all calls to the LayoutSdk happen through the wrapper (and the getLayoutSDK() method doesn't have to exist)
 */
export class LayoutSDKWrapper {
  userInfo: IAuthenticatedUserInfo | undefined
  readonly layoutSDK: LayoutSDK

  // We want to leverage a lock on requesting tokens in the case where we are
  // getting a new token and another action on the client side has occurred
  // which requires an access token but we are already in the middle of getting
  // a new one. We should drop this second request and lift the lock when we
  // apply the token from the first onSDKUnauthorized request
  tokenRefreshLock: boolean

  authContext?: IAuthContext

  private setupComplete: boolean
  onSessionExpired?: () => void

  constructor(options: CreateOptions) {
    this.userInfo = options.userInfo
    this.layoutSDK = this.createLayoutSDK(options)
    this.tokenRefreshLock = false
    this.setupComplete = false
    this.onSessionExpired = options.onSessionExpired
  }

  // The following order is enforced to ensure the LayoutSDK is initialized properly:
  // 1) Await ready
  // 2) Await render
  // 3) Await login (this is a separate method to allow callers to execute code between these calls)
  async setup(options?: { renderElementId?: string, langCode?: string, authContext?: IAuthContext, theme?: OneNoteTheme, platform?: string }): Promise<void> {
    this.authContext = options && options.authContext
    await this.layoutSDK.ready
    if (options && options.langCode) {
      this.layoutSDK.changeLanguage(options.langCode)
    }
    if (options && options.renderElementId) {
      await this.layoutSDK.render(options.renderElementId)
    }
    if (options && options.theme === 'Dark') {
      this.layoutSDK.enableDarkMode(true)
    } else {
      this.layoutSDK.enableDarkMode(false)
    }

    // tslint:disable-next-line:no-floating-promises
    this.getUserProfilePicture()

    this.setupComplete = true
  }

  // Fetch Profile Picture for user
  async getUserProfilePicture(): Promise<void> {
    if (this.userInfo && this.userInfo?.authType && !isMsa(this.userInfo.authType)) {
      const tokenInfo = await tryGetOAuthTokenWithoutUserAction()
      if (!tokenInfo || !tokenInfo.access_token) {
        Log.logger.warn('UserInfo was successfully retrieved, but was unable to get tokenInfo. Aborting photo fetch!')
        return
      }
      try {
        const tokenGetter = GetTokenFactory.createO365(tokenInfo.access_token, this.userInfo.tenantId!, false /* forceRefresh */)
        const graphToken = await tokenGetter.getGraphUserInfoToken()
        if (graphToken && this.userInfo && this.userInfo.tenantId) {
          this.layoutSDK.setGraphToken(graphToken)
        }
      } catch (e) {
        Log.logger.warn('Fetching of profile picture failed')
      }
    }
  }

  async login(): Promise<void> {
    if (!this.setupComplete) {
      throw logAndBuildError('setup() MUST be called before login(). Aborting!')
    }

    // Either sync sticky notes, or show an error dialog
    if (this.userInfo) {
      try {
        await this.getInitialTokensForSDK(this.layoutSDK, this.userInfo, this.authContext)
      } catch (error) {
        if (O365ExchangeTokenError.isO365ExchangeTokenError(error)) {
          if (error.failureCase === O365ExchangeTokenFailureCase.ResourceIsDisabled) {
            Log.logger.error(`The user appears to not have an exchange subscription - showing the error. ${getErrorAsString(error)}`)
            this.layoutSDK.showExchangeLicenseUnavailable()
          } else {
            Log.logger.error(`A generic exchange token error ${error.failureCase} was thrown - showing reload banner`)
            this.onSessionExpired !== undefined ? this.onSessionExpired() : showSessionExpired(this.layoutSDK)
          }
          return
        }

        Log.logger.error(`Error getting tokens for SDK initially! Showing session expired banner. ${getErrorAsString(error)}`)
        this.onSessionExpired !== undefined ? this.onSessionExpired() : showSessionExpired(this.layoutSDK)
      }
    } else {
      // Only when running locally, we tolerate no user information here. Else we show the reload banner
      if (!shouldMockExternalApis()) {
        Log.logger.error(`Showing session expired banner because the user information was undefined`)
        this.onSessionExpired !== undefined ? this.onSessionExpired() : showSessionExpired(this.layoutSDK)
      }
    }
  }

  private createLayoutSDK(options: CreateOptions): LayoutSDK {
    const { userInfo, onDetailViewChanged, hostApp, disableMediaDownload, isEdgeHub, isFeedEnabled,
      enableOneNotePages, noteReferenceOpenHandler, isHtmlRenderingEnabled, telemetrySink } = options
    const telemetrySDKCallbacks = new LayoutSDKTelemetryCallbacks()
    const userContextForLogging = getOTelLoggingUserContextFromUserInformation(userInfo)
    initializeAllIcons()
    const loggingOptions: ILoggingOptions | undefined = userContextForLogging ? {
      userId: userContextForLogging.userId,
      userIdType: userInfo?.authType === 'o365' ? 'UserObjectId' : 'MSACID',
      oMSTenantId: userContextForLogging.tenantId,
      appId: Constants.stickyNotesOndcAppIdForLogging,
      hostApp: hostApp,
      hostSessionId: GlobalVariables.environmentSettings.sessionId,
      samplingDisabled: GlobalVariables.environmentSettings.ring === Constants.RING_TYPE.INTERNAL || userContextForLogging.tenantId === Constants.WELL_KNOWN_MICROSOFT_TENANT_ID,
    } : undefined
    return new LayoutSDK({
      appName: Constants.notesFabricApiCreatedByApp,
      authType: userInfo?.authType,
      appVersion: GlobalVariables.environmentSettings.version,
      onSignedOut: telemetrySDKCallbacks.onSDKSignOut,
      onUnauthorized: this.onSDKUnauthorizedCallback,
      onNoteDeleting: telemetrySDKCallbacks.onNoteDeleted,
      onNotesUpdated: telemetrySDKCallbacks.onNotesUpdated,
      uniqueUserId: userInfo ? userInfo.upn : undefined,
      onDetailViewChanged,
      onTelemetry: telemetrySDKCallbacks.onTelemetry,
      storage: true,
      sync: !!userInfo,
      showAppName: false,
      showAppHeader: true,
      loggingOptions,
      disableMediaDownload,
      allowEmptyNotesCollection: true,
      isFeedEnabled: !!isFeedEnabled, /* Default to false so feed is not enabled in ONDC/SN */
      isEdgeHub: isEdgeHub,
      isConnectedNotesEnabled: !isEdgeHub,
      enableOneNotePages: !!enableOneNotePages,
      noteListItemRenderWrapper: this.renderNoteItem,
      noteReferenceOpenHandler: noteReferenceOpenHandler,
      isHtmlRenderingEnabled: !!isHtmlRenderingEnabled,
      telemetrySink: telemetrySink,
      isNoteRefCachingEnabled: true,
      isRelatedNotesSectionEnabled: false,
    })
  }

  // This can't be extracted into its own function as it has a circular reference to LayoutSDK
  onSDKUnauthorizedCallback = async (): Promise<void> => {
    Log.logger.info('OnSDKUnauthorized called - attempting to refresh token...')

    if (this.tokenRefreshLock) {
      Log.logger.info('onSDKUnauthorizedCallback already called and still working')
      return
    }

    if (!this.userInfo) {
      // TODO: what should we show in the UI for this error path?
      // "Something went horribly wrong, please contact support."
      // THIS IS A DEAD END
      logAndBuildError('OnSdkUnauthorized should not be called if no user information is present')
      this.onSessionExpired !== undefined ? this.onSessionExpired() : showSessionExpired(this.layoutSDK)
      return
    }

    this.tokenRefreshLock = true
    try {
      await this.refreshToken()
      Log.logger.info('Successfully refreshed the token')
      this.tokenRefreshLock = false
    } catch (e) {
      Log.logger.error(`Error occurred while refreshing access token in active session: ${getErrorAsString(e)}, redirect user to sign in.`)
      if (!this.layoutSDK.layoutSDKOptions.isEdgeHub) {
        this.onSessionExpired !== undefined ? this.onSessionExpired() : showSessionExpired(this.layoutSDK)
      } else {
        const isMsaAccount: boolean = isMsaAccountLogin()
        redirectToAuth(isMsaAccount)
      }
    }
  }

  private async refreshToken(): Promise<void> {
    const authType = this.userInfo && this.userInfo.authType
    const refreshTokenActivity = new RefreshTokenForSdkActivity(authType)

    if (!this.userInfo || !authType) {
      refreshTokenActivity.finishWithFailure('Attempted to refresh when current userInfo is not present!')
      throw logAndBuildError('While attempting to refresh, the current userInfo should be present!')
    }

    // In the case of msa_implicit, we do not need to refresh the original root access token
    // In the case of o365, we need to refresh before we exchange tokens
    // In the case of msa, we need to refresh using the auth context
    if (authType === 'o365' || (isMsa(authType) && this.authContext)) {
      try {
        if (this.authContext) {
          await this.authContext.refresh()
        } else {
          await refreshAccessToken()
        }
      } catch (error) {
        refreshTokenActivity.finishWithFailure(`Failed to refresh ${authType} token`, error)
        throw logAndBuildError(`Failed to refresh token! ${getErrorAsString(error)}`)
      }
    }

    try {
      await this.refreshTokensForSDK(this.layoutSDK, this.userInfo!, this.authContext)
      refreshTokenActivity.finishWithSuccess()
    } catch (error) {
      refreshTokenActivity.finishWithFailure('Failed to get tokens for SDK', error)
      throw logAndBuildError(`Failed to get tokens for sdk! ${getErrorAsString(error)}`)
    }
  }

  // Needed for mocking in tests
  async refreshTokensForSDK(layoutSDK: LayoutSDK, userInfo: IAuthenticatedUserInfo, authContext?: IAuthContext): Promise<void> {
    return await refreshTokensForSDK(layoutSDK, userInfo, authContext)
  }

  // Needed for mocking in tests
  async getInitialTokensForSDK(layoutSDK: LayoutSDK, userInfo: IAuthenticatedUserInfo, authContext?: IAuthContext): Promise<void> {
    return await getInitialTokensForSDK(layoutSDK, userInfo, authContext)
  }

  private renderNoteItem = (noteId: string, children?: React.ReactNode): JSX.Element => {
    const dragStart = async (event: React.DragEvent<HTMLDivElement>) => {
      // React creates synthetic events that wrap native events
      // These synthetic events are recycled and therefore can become invalid
      // when passed into async contexts.
      // event.persist() tells React that we plan on using this asynchronously
      // which will ensure it doesn't get recycled for other events
      // That doesn't mean we are responsible for garbage collection.
      event.persist()

      const note = await this.layoutSDK.notesSDK.getLocalNote(noteId)
      if (!note) {
        event.stopPropagation()
        return
      }
        // If we are dragging an empty note, let's cancel the event early on so that
        // we don't show the drag visual and confuse the user if we know the drop
        // will be a no op beforehand
      const htmlContent = await note.getAsHtml()
      if (!htmlContent) {
        event.stopPropagation()
        return
      }

        // Since everything looks good, let's set our intent from this event
        // effectAllowed could be used by the target of the drag over or drop events
        // to show some visual. In OneNote UWP for example, 'copy' will show a
        // Copy icon under the cursor while dragging to indicate to the user that when
        // they drop this note, it will be copied into the canvas.
        // One alternative would be 'move' which would show a 'Cut' icon that will
        // delete the note from the Feed when dropped elsewhere if that event is
        // successful (The feed would still have to implement that deletion)
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('text/html', htmlContent)
    }

    return (
      <div draggable={ true } onDragStart={ dragStart }>
          { children }
      </div>
    )
  }
}

type CreateOptions = {
  userInfo: IAuthenticatedUserInfo | undefined
  hostApp: string
  onSessionExpired?: () => void
} & Partial<LayoutSDKOptions>
